import { useEffect, useState } from "react";
import { css } from "@packages/panda-generated/css";
import { hstack } from "@packages/panda-generated/patterns";
import { TbCheck, TbX, TbAlertCircle, TbInfoCircle } from "react-icons/tb";

export type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationProps {
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  onClose: () => void;
  isVisible: boolean;
}

export function Notification({
  type,
  title,
  message,
  duration = 4000,
  onClose,
  isVisible,
}: NotificationProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return <TbCheck size={20} />;
      case "error":
        return <TbX size={20} />;
      case "warning":
        return <TbAlertCircle size={20} />;
      case "info":
        return <TbInfoCircle size={20} />;
      default:
        return <TbInfoCircle size={20} />;
    }
  };

  const getColors = () => {
    switch (type) {
      case "success":
        return {
          bg: "green.50",
          border: "green.200",
          iconColor: "green.600",
          titleColor: "green.800",
          messageColor: "green.700",
        };
      case "error":
        return {
          bg: "red.50",
          border: "red.200",
          iconColor: "red.600",
          titleColor: "red.800",
          messageColor: "red.700",
        };
      case "warning":
        return {
          bg: "yellow.50",
          border: "yellow.200",
          iconColor: "yellow.600",
          titleColor: "yellow.800",
          messageColor: "yellow.700",
        };
      case "info":
        return {
          bg: "blue.50",
          border: "blue.200",
          iconColor: "blue.600",
          titleColor: "blue.800",
          messageColor: "blue.700",
        };
      default:
        return {
          bg: "gray.50",
          border: "gray.200",
          iconColor: "gray.600",
          titleColor: "gray.800",
          messageColor: "gray.700",
        };
    }
  };

  const colors = getColors();

  if (!isVisible && !isAnimating) return null;

  return (
    <div
      className={css({
        position: "fixed",
        top: 4,
        right: 4,
        zIndex: 9999,
        minWidth: "320px",
        maxWidth: "480px",
        bg: colors.bg,
        border: "1px solid",
        borderColor: colors.border,
        borderRadius: "lg",
        boxShadow: "0 10px 15px rgba(0, 0, 0, 0.1)",
        p: 4,
        transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        opacity: isAnimating ? 1 : 0,
        transition: "all 0.3s ease-in-out",
      })}
    >
      <div className={hstack({ gap: 3, alignItems: "flex-start" })}>
        {/* Icon */}
        <div
          className={css({
            color: colors.iconColor,
            flexShrink: 0,
            mt: 0.5,
          })}
        >
          {getIcon()}
        </div>

        {/* Content */}
        <div className={css({ flex: 1, minWidth: 0 })}>
          <h4
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: colors.titleColor,
              mb: message ? 1 : 0,
            })}
          >
            {title}
          </h4>
          {message && (
            <p
              className={css({
                fontSize: "sm",
                color: colors.messageColor,
                lineHeight: "relaxed",
              })}
            >
              {message}
            </p>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className={css({
            color: colors.iconColor,
            _hover: {
              color: colors.titleColor,
            },
            flexShrink: 0,
            p: 1,
            borderRadius: "sm",
            _hover: {
              bg: "rgba(0, 0, 0, 0.05)",
            },
          })}
        >
          <TbX size={16} />
        </button>
      </div>
    </div>
  );
}
